if (!customElements.get('media-gallery')) {

  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;

        this._pendingActivation = null;
      }

      connectedCallback() {
        const idxAttr = this.dataset.activeSlideIndex;
        if (idxAttr !== undefined) {
          const n = parseInt(idxAttr, 10);
          if (Number.isInteger(n) && n >= 0) {
            this._pendingActivation = { activeSlideIndex: n };
          }
        }
        const mediaIdAttr = this.dataset.activeMediaId;
        if (mediaIdAttr) {
          this._pendingActivation = { activeMediaId: String(mediaIdAttr) };
        }

        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

          const thumbsBreakpoints = {
            461: { slidesPerView: 5, direction: 'horizontal' },
            768: { spaceBetween: mediaItemGap },
          };

        this.thumbsOptions = {
          slidesPerView: 4,
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: {                  
            enabled: true,
            momentum: true,
            sticky: false,            
            momentumBounce: false,
            momentumRatio: 0.7,
            momentumVelocityRatio: 0.35,
            minimumVelocity: 0.02,
          },
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: thumbsBreakpoints,

          mousewheel: {                
            enabled: true,
            forceToAxis: true,          
            releaseOnEdges: true,
            sensitivity: 0.6,           
            thresholdDelta: 1,          
            thresholdTime: 0.5,
          },

          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {

            const thumbsModules = [];
            if (window.FoxTheme?.Swiper?.Mousewheel) thumbsModules.push(FoxTheme.Swiper.Mousewheel);
            if (window.FoxTheme?.Swiper?.FreeMode)   thumbsModules.push(FoxTheme.Swiper.FreeMode);

            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions, thumbsModules);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 0,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
          this.applyActivationIfReady();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
          this.applyActivationIfReady();
        });
      }

      ensureThumbVisible(index, animate = true) {
        const t = this.thumbsInstance && this.thumbsInstance.slider;
        if (!t) return;

        const slide = t.slides[index];
        if (!slide) return;

        const isH = t.isHorizontal();
        const slideStart = slide.swiperSlideOffset;                 
        const slideSize  = isH ? slide.offsetWidth : slide.offsetHeight;
        const viewport   = t.size;                                  
        const cur        = -t.translate;                             
        const max        = -t.maxTranslate();                      

        let target = cur;
        if (slideStart < cur) target = slideStart;                                   
        else if (slideStart + slideSize > cur + viewport)                 
          target = slideStart + slideSize - viewport;

        if (target < 0) target = 0;
        if (target > max) target = max;

        if (animate && t.params.speed > 0) t.setTransition(t.params.speed);
        t.setTranslate(-target);
        t.updateActiveIndex();
        t.updateSlidesClasses();
        if (animate && t.params.speed > 0) t.setTransition(0);
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            this.ensureThumbVisible(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        const featuredMediaId = this.getFeaturedMediaId(variant);
        if (!featuredMediaId) return false;

        const slider = this.sliderInstance && this.sliderInstance.slider;
        if (slider) {
          const slideIndex = this.getRealIndexByMediaId(featuredMediaId);
          if (slideIndex < 0) return false;
          slider.slideToLoop(slideIndex, 0, false);
          if (slider.slides[slider.activeIndex]) {
            this.playActiveMedia(slider.slides[slider.activeIndex]);
          }
          return true;
        }

        this.sortMediaItems({
          featured_media: {
            id: featuredMediaId,
          },
        });
        return true;
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }

      getRealIndexByMediaId(mediaId) {
        const base = this.elements?.viewer || this;
        const realSlides = Array.from(base.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)'));
        for (let i = 0; i < realSlides.length; i++) {
          const host = realSlides[i].querySelector('[data-media-id]');
          if (host && String(host.dataset.mediaId) === String(mediaId)) return i;
        }
        return -1;
      }

      getFeaturedMediaId(variant) {
        if (!variant) return null;

        if (variant.featured_media && typeof variant.featured_media === 'object' && variant.featured_media.id !== undefined) {
          return String(variant.featured_media.id);
        }

        if (variant.featured_media_id !== undefined && variant.featured_media_id !== null) {
          return String(variant.featured_media_id);
        }

        if (variant.featured_media !== undefined && variant.featured_media !== null && typeof variant.featured_media !== 'object') {
          return String(variant.featured_media);
        }

        return null;
      }

      applyActivationIfReady() {
  if (!this._pendingActivation) return;

  const { activeSlideIndex, activeMediaId } = this._pendingActivation;
  const slider = this.sliderInstance && this.sliderInstance.slider;

  if (slider && slider.initialized) {
    let targetIndex = -1;

    if (Number.isInteger(activeSlideIndex) && activeSlideIndex >= 0) {
      targetIndex = activeSlideIndex;
    } else if (activeMediaId) {
      targetIndex = this.getRealIndexByMediaId(activeMediaId);
    }

    if (targetIndex >= 0) {
      slider.slideToLoop(targetIndex, 0, false);
      this._pendingActivation = null;
      return;
    }
  }

  const productInfo = this.closest('product-info');
  const variant = productInfo?.currentVariant;
  if (variant) {
    this.setActiveMedia(variant);
    this._pendingActivation = null;
    return;
  }

  const base = this.elements?.viewer || this;
  const list = base.querySelector('.swiper-wrapper') || base;
  if (!list) return;

  const mo = new MutationObserver(() => {
    if (slider && slider.initialized) {
      let idx = -1;
      if (Number.isInteger(activeSlideIndex) && activeSlideIndex >= 0) {
        idx = activeSlideIndex;
      } else if (activeMediaId) {
        idx = this.getRealIndexByMediaId(activeMediaId);
      }
      if (idx >= 0) {
        slider.slideToLoop(idx, 0, false);
        this._pendingActivation = null;
        mo.disconnect();
        clearTimeout(timeoutId);
        return;
      }
    }

    const pi = this.closest('product-info');
    const v = pi?.currentVariant;
    if (v) {
      this.setActiveMedia(v);
      this._pendingActivation = null;
      mo.disconnect();
      clearTimeout(timeoutId);
    }

  });

  mo.observe(list, { childList: true, subtree: true });

  const timeoutId = setTimeout(() => {
    mo.disconnect();
  }, 5000);
}

    }
  );
}
